import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  claudeSignInResultSchema,
  environmentReportSchema,
  GROOMING_QUEUE_SETUP_COPY,
  isGroomingQueueSetupCode,
  TOOLBOX_DOWNLOAD_PAGE_PATH,
  type GroomingQueueActionResult,
  type GroomingQueueFixAction,
  type GroomingQueuePause,
  type GroomingQueueState,
  type GroomingRepoHost,
  type GroomingRepoStatus,
  type ToolboxBridge,
} from "@ttc-spectree/toolbox-contract";
import { groomingErrorMessage } from "@/lib/grooming/error-messages";
import { plainBridgeError } from "@/lib/toolbox/bridge-error";
import { GROOMING_QUEUE_PATH } from "@/lib/grooming/paths";
import {
  isFixItSetupStep,
  QUEUE_FIXED_PARAM,
  queueFixSetupPath,
  type FixItSetupStep,
} from "@/lib/toolbox/page-names";

/**
 * "Fix it" on a paused run queue (epic PI-SPEC-SW-E290, feature
 * PI-SPEC-SW-1479; Claude Design frames B2 and B3; 01-roles.md section 9.6).
 *
 * Fix it reuses PKG-PM-H's actions and asks for no new app contract number:
 * - `claude-sign-in`: Claude's sign-in from the Queue page (`app.signInToClaude`;
 *   an older app opens the browser sign-in and the page re-checks until it is done);
 * - `repo-sign-in`: "Sign in to the repos" from the Queue page (`grooming.signInToRepos`);
 * - `check-connection`: ask the app to start again now instead of waiting for its next minute;
 * - `open-setup-step`: open that step of Setup, which comes back to the Queue with `?fixed=1`;
 * - `get-update`: open the download page (the queue is kept and starts again after the reinstall).
 *
 * Fix it never works around a failure (2026-10-19): it runs the one repair
 * and then asks the app to resume. The app re-runs its first-run check and
 * stays paused if the problem remains, so a fix that did not take shows the
 * same banner again.
 */

/** One Fix it: what it does and, for `open-setup-step`, which step. */
export interface FixIt {
  action: GroomingQueueFixAction;
  setupStep: FixItSetupStep | null;
}

/**
 * Where the repository of a `REPO_AUTH_REQUIRED` pause lives, when the app
 * says (epic PI-SPEC-SW-E318). The pause schema is loose, so a newer app may
 * add `host`; anything but `"github"` (an older app sends nothing) is
 * Azure DevOps. Pure.
 */
export function pauseRepoHost(pause: object | null | undefined): GroomingRepoHost {
  const host = pause && "host" in pause ? (pause as { host?: unknown }).host : undefined;
  return host === "github" ? "github" : "ado";
}

/**
 * The Fix it for a pause, from this page's own contract version (never the
 * app's `pause.fix`, which an older app fills with its own command texts).
 * Null for a pause that is not `setup`. A setup pause whose code this version
 * does not know opens Setup at the top. A repository sign-in for a GitHub
 * repository (`host`, else the pause's own `host`) opens Setup's Project code
 * step, where "Sign in to GitHub" is (epic PI-SPEC-SW-E318).
 */
export function fixItForPause(
  pause: Pick<GroomingQueuePause, "reason" | "errorCode"> | null,
  host?: GroomingRepoHost | null
): FixIt | null {
  if (pause?.reason !== "setup") return null;
  if (!isGroomingQueueSetupCode(pause.errorCode)) return { action: "open-setup-step", setupStep: null };
  if (pause.errorCode === "REPO_AUTH_REQUIRED" && (host ?? pauseRepoHost(pause)) === "github") {
    return { action: "open-setup-step", setupStep: "project-code" };
  }
  const copy = GROOMING_QUEUE_SETUP_COPY[pause.errorCode];
  const step = copy.setupStep ?? null;
  return { action: copy.fixAction, setupStep: isFixItSetupStep(step) ? step : null };
}

/**
 * One plain sentence under the banner: what Fix it does and what happens
 * after it works (frame B2: "Fix it opens Claude's sign-in in your browser,
 * and the queue starts again by itself once you're signed in.").
 */
export function fixItHint(fix: FixIt): string {
  switch (fix.action) {
    case "claude-sign-in":
      return "Fix it opens Claude's sign-in in your browser, and the queue starts again by itself once you're signed in.";
    case "repo-sign-in":
      return "Fix it signs you in to the project's code from here, and the queue starts again by itself once that works.";
    case "check-connection":
      return "Fix it checks the connection now, and the queue starts again by itself once it's back.";
    case "get-update":
      return "Fix it opens the download page for the latest version. The queue is kept and starts again after the reinstall.";
    case "open-setup-step":
      return fix.setupStep === "azure-devops"
        ? "Fix it opens the Azure DevOps step of Setup. Once it passes you come straight back here, and the queue starts again."
        : fix.setupStep === "project-code"
          ? "Fix it opens the Project code step of Setup, where Sign in to GitHub signs you in. Once it passes you come straight back here, and the queue starts again."
          : fix.setupStep === null
          ? "Fix it opens Setup. Once every step passes, come back here and the queue starts again."
          : "Fix it opens the step of Setup that installs it. Once it passes you come straight back here, and the queue starts again.";
  }
}

/** What a Fix it did. */
export type FixItResult =
  | { kind: "resumed"; message: string }
  | { kind: "navigated" }
  | { kind: "failed"; message: string };

/** What a Fix it needs from the page. */
export interface FixItContext {
  bridge: ToolboxBridge;
  /** The queue's resume action (`grooming.resumeQueue()`). */
  resume: () => Promise<GroomingQueueActionResult>;
  /** Open a SpecTree page. */
  navigate: (to: string) => void;
  /** The project whose repositories need a sign-in (the first stopped or waiting entry's). */
  project?: string | null | undefined;
  /** For an older app's Claude sign-in: how often to re-check (default 3 s) and for how long (default 5 min). */
  pollMs?: number;
  timeoutMs?: number;
  /** For tests: wait `ms`, and the clock. */
  wait?: (ms: number) => Promise<void>;
  now?: () => number;
}

/** The fallback Claude sign-in re-checks this often. */
export const CLAUDE_SIGN_IN_POLL_MS = 3_000;

/** And gives up after this long. */
export const CLAUDE_SIGN_IN_TIMEOUT_MS = 5 * 60_000;

const SIGN_IN_FAILED = "Claude's sign-in didn't finish. Try Fix it again.";
const SIGN_IN_TIMED_OUT = "Claude's sign-in didn't finish within 5 minutes. Try Fix it again.";
const REPO_SIGN_IN_FAILED = "The sign-in to the project's code didn't work. Try Fix it again.";
const NO_PROJECT = "There is no project to sign in to. Copy the details for the Admin.";
const FIX_FAILED = "That didn't work. Try Fix it again in a moment.";

/** The sentence after a successful repair, while the app starts again. */
export const FIX_IT_RESUMED: Readonly<Record<"claude-sign-in" | "repo-sign-in" | "check-connection", string>> = {
  "claude-sign-in": "Signed in. Starting again…",
  "repo-sign-in": "Signed in to the project's code. Starting again…",
  "check-connection": "Checking the connection. The queue starts again once it's back.",
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Ask the app to start again after a repair; its refusal in plain words. */
async function resumeAfter(
  ctx: FixItContext,
  action: keyof typeof FIX_IT_RESUMED
): Promise<FixItResult> {
  const result = await ctx.resume();
  if (result.ok) return { kind: "resumed", message: FIX_IT_RESUMED[action] };
  return {
    kind: "failed",
    message: result.errorCode === "UNKNOWN" ? result.message : groomingErrorMessage(result.errorCode).cause,
  };
}

/** True when an older app's environment check says Claude is signed in. */
async function claudeSignedIn(bridge: ToolboxBridge): Promise<boolean> {
  try {
    const parsed = environmentReportSchema.safeParse(await bridge.app.checkEnvironment());
    return parsed.success && parsed.data.shared["claude-signed-in"].status === "ok";
  } catch {
    return false;
  }
}

async function claudeSignIn(ctx: FixItContext): Promise<FixItResult> {
  const { bridge } = ctx;
  if (typeof bridge.app.signInToClaude === "function") {
    const parsed = claudeSignInResultSchema.safeParse(await bridge.app.signInToClaude());
    if (!parsed.success || !parsed.data.ok) {
      const reason = parsed.success ? (parsed.data.reason?.trim() ?? "") : "";
      return { kind: "failed", message: reason.length > 0 ? reason : SIGN_IN_FAILED };
    }
    return resumeAfter(ctx, "claude-sign-in");
  }
  // An app older than PKG-PM-H: open the browser sign-in, then re-check until Claude is signed in.
  await bridge.app.openClaudeSignIn();
  const wait = ctx.wait ?? sleep;
  const now = ctx.now ?? Date.now;
  const pollMs = ctx.pollMs ?? CLAUDE_SIGN_IN_POLL_MS;
  const deadline = now() + (ctx.timeoutMs ?? CLAUDE_SIGN_IN_TIMEOUT_MS);
  while (now() < deadline) {
    await wait(pollMs);
    if (await claudeSignedIn(bridge)) return resumeAfter(ctx, "claude-sign-in");
  }
  return { kind: "failed", message: SIGN_IN_TIMED_OUT };
}

async function repoSignIn(ctx: FixItContext): Promise<FixItResult> {
  const project = ctx.project?.trim();
  if (!project) return { kind: "failed", message: NO_PROJECT };
  const grooming = ctx.bridge.grooming;
  if (!grooming) return { kind: "failed", message: REPO_SIGN_IN_FAILED };
  const statuses: GroomingRepoStatus[] = await grooming.signInToRepos(project);
  const stuck = statuses.find((status) => status.state === "auth-required" || status.state === "error");
  if (stuck) {
    const message = stuck.message?.trim() ?? "";
    return { kind: "failed", message: message.length > 0 ? message : REPO_SIGN_IN_FAILED };
  }
  return resumeAfter(ctx, "repo-sign-in");
}

/**
 * Run one Fix it: the repair, then (for the repairs made on the Queue page)
 * a resume. A rejected bridge call reads as plain words.
 */
export async function runFixIt(fix: FixIt, ctx: FixItContext): Promise<FixItResult> {
  try {
    switch (fix.action) {
      case "claude-sign-in":
        return await claudeSignIn(ctx);
      case "repo-sign-in":
        return await repoSignIn(ctx);
      case "check-connection":
        return await resumeAfter(ctx, "check-connection");
      case "open-setup-step":
        ctx.navigate(queueFixSetupPath(fix.setupStep));
        return { kind: "navigated" };
      case "get-update":
        ctx.navigate(TOOLBOX_DOWNLOAD_PAGE_PATH);
        return { kind: "navigated" };
    }
  } catch (error) {
    return { kind: "failed", message: plainBridgeError(error, FIX_FAILED) };
  }
}

/**
 * Back on the Queue from a Fix it on Setup (`?fixed=1`): once the queue is
 * read, ask the app to start again exactly once if it is still paused for
 * setup, and drop the parameter so a reload or Back does not ask again.
 */
export function useResumeAfterFixReturn(
  state: GroomingQueueState | null,
  resume: () => Promise<GroomingQueueActionResult>,
  onResult?: (result: GroomingQueueActionResult) => void
): void {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const fixed = params.get(QUEUE_FIXED_PARAM) !== null;
  const handled = useRef(false);

  useEffect(() => {
    if (!fixed || state === null || handled.current) return;
    handled.current = true;
    if (state.status === "paused" && state.pause?.reason === "setup") {
      void resume().then((result) => {
        onResult?.(result);
      });
    }
    void navigate(GROOMING_QUEUE_PATH, { replace: true });
  }, [fixed, state, resume, navigate, onResult]);
}
